/**
 * nw-popups-group.js
 *
 * 弹窗组件库，包含三部分：
 *   1. <nw-modal> 自定义元素 — 弹窗本体
 *   2. initHashPopupTriggers — href 链接触发
 *   3. initPopupTabs — 弹窗内 Tab 切换
 *
 * ── 打开弹窗的两种方式 ──────────────────────────────────────
 *
 *   方式 A（推荐）：aria-controls
 *     <button aria-controls="PopupModal-login">登录</button>
 *     点击切换 show / hide
 *
 *   方式 B：href 锚点（仅支持 #PopupModal- 前缀）
 *     <a href="#PopupModal-login">登录</a>
 *     仅打开，不切换；不改 URL，不监听 hashchange
 *     若同时写了 aria-controls，走方式 A，避免重复触发
 *
 * ── 弹窗 HTML 结构 ───────────────────────────────────────────
 *
 *   <nw-modal id="PopupModal-xxx" class="nw-popup-modal" hidden>
 *     <div class="overlay">...</div>   <!-- 点击遮罩关闭 -->
 *     <div class="content-wrapper">...</div>
 *   </nw-modal>
 */

(function () {
  if (customElements.get('nw-modal')) return;

  // ===========================================================================
  // nw-modal — 弹窗自定义元素
  // ===========================================================================

  class NwModal extends HTMLElement {
    static get observedAttributes() {
      return ['open', 'id'];
    }

    constructor() {
      super();
      // 用于 disconnectedCallback 时统一解绑事件
      this.abortController = null;
    }

    /** 遮罩层，点击可关闭弹窗 */
    get overlay() {
      return this.querySelector('.overlay, [class*="overlay"]');
    }

    /** 所有 aria-controls 指向本弹窗 id 的触发元素 */
    get controls() {
      if (!this.id) return [];
      return Array.from(document.querySelectorAll(`[aria-controls="${this.id}"]`));
    }

    get open() {
      return this.hasAttribute('open');
    }

    connectedCallback() {
      if (this.abortController) this.abortController.abort();
      this.abortController = new AbortController();
      const { signal } = this.abortController;

      // 触发器：aria-controls 绑定，点击切换开关
      this.controls.forEach((button) => {
        button.addEventListener('click', (e) => {
          e.preventDefault();
          this.open ? this.hide() : this.show();
        }, { signal });
      });

      // Esc 关闭
      document.addEventListener('keyup', (e) => {
        if (e.code === 'Escape' && this.open) this.hide();
      }, { signal });

      // 点击遮罩关闭（仅直接点在 overlay 上时）
      if (this.overlay) {
        this.overlay.addEventListener('click', (e) => {
          if (e.target === this.overlay) this.hide();
        }, { signal });
      }
    }

    disconnectedCallback() {
      this.abortController?.abort();
    }

    /**
     * open 属性变化时执行开关动画与 DOM 操作。
     * 打开：移入 body → 禁止滚动 → 加 active 触发动画
     * 关闭：去 active → 等 transition 结束 → hidden → 移回原位
     */
    attributeChangedCallback(name, oldValue, newValue) {
      if (name !== 'open') return;

      // 同步触发器的 aria-expanded
      this.controls.forEach((button) => {
        button.setAttribute('aria-expanded', newValue !== null ? 'true' : 'false');
      });

      if (oldValue === null && newValue !== null) {
        this._onOpen();
      } else if (oldValue !== null && newValue === null) {
        this._onClose();
      }
    }

    _onOpen() {
      this.hidden = false;
      this.removeAttribute('inert');

      // 挂到 body，避免被父级 overflow / z-index 裁切
      if (this.parentElement !== document.body) {
        this.originalParent = this.parentElement;
        document.body.appendChild(this);
      }

      document.body.classList.add('has-modal-open');

      requestAnimationFrame(() => {
        this.setAttribute('active', '');
        this.initTabScrollers();
      });
    }

    _onClose() {
      this.setAttribute('inert', '');
      this.removeAttribute('active');
      document.body.classList.remove('has-modal-open');
      this.dispatchEvent(new CustomEvent('nw:modal:close', { bubbles: true }));

      const restore = () => {
        if (this.hasAttribute('open')) return;
        this.hidden = true;
        if (this.originalParent && this.parentElement === document.body) {
          this.originalParent.appendChild(this);
          this.originalParent = null;
        }
      };

      // 有遮罩时等关闭动画结束再 hidden，否则立即隐藏
      if (this.overlay) {
        this.overlay.addEventListener('transitionend', restore, { once: true });
      } else {
        restore();
      }
    }

    show() {
      if (this.open) return;
      this.setAttribute('open', '');
    }

    hide() {
      if (!this.open) return;
      this.removeAttribute('open');
    }

    /** 弹窗打开后初始化内部的 nw-tab-scroller（延迟挂载的组件需要重新 init） */
    initTabScrollers() {
      this.querySelectorAll('nw-tab-scroller').forEach((scroller) => {
        if (scroller.isInitialized) return;
        scroller.dataset.initialized = 'false';
        scroller.isInitialized = false;
        if (scroller.tryInit) {
          scroller.tryInit();
        } else {
          requestAnimationFrame(() => scroller.init());
        }
      });
    }
  }

  customElements.define('nw-modal', NwModal);

  // ===========================================================================
  // href 触发 — <a href="#PopupModal-xxx">
  // ===========================================================================

  function initHashPopupTriggers() {
    if (window.__nwPopupHashTriggerBound) return;
    window.__nwPopupHashTriggerBound = true;

    document.addEventListener('click', (e) => {
      const link = e.target.closest('a[href^="#PopupModal-"]');
      if (!link || link.hasAttribute('aria-controls')) return;

      const id = link.getAttribute('href').slice(1);
      const modal = document.getElementById(id);
      if (!modal || typeof modal.show !== 'function') return;

      e.preventDefault();
      modal.show();
    });
  }

  // ===========================================================================
  // Popup Tabs — 弹窗内 .tab-item / .popup-tab-content 切换
  // ===========================================================================

  function initPopupTabs(sectionRoot, defaultTarget = null) {
    if (!sectionRoot) return;

    const tabItems = sectionRoot.querySelectorAll('.tab-item');
    const tabContents = sectionRoot.querySelectorAll('.popup-tab-content');

    function activateTab(item) {
      const target = item.dataset.target;
      tabItems.forEach((i) => i.classList.remove('active'));
      item.classList.add('active');
      tabContents.forEach((c) => c.classList.add('hidden'));
      sectionRoot.querySelector(`#${target}`)?.classList.remove('hidden');
    }

    tabItems.forEach((item) => {
      item.addEventListener('click', () => activateTab(item));
    });

    const defaultItem = defaultTarget
      ? sectionRoot.querySelector(`.tab-item[data-target="${defaultTarget}"]`)
      : sectionRoot.querySelector('.tab-item.active') || tabItems[0];

    defaultItem && activateTab(defaultItem);
  }

  function initAllPopupTabs() {
    document.querySelectorAll('.nw-popup-modal').forEach(initPopupTabs);
  }

  // ===========================================================================
  // 启动
  // ===========================================================================

  function initAll() {
    initHashPopupTriggers();
    initAllPopupTabs();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }
})();
